import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { WorkshopLocation } from "../hooks/useWorkshopLocations";

const MARKER_ICON = L.divIcon({
  className: "loc-marker",
  html: `
    <div class="loc-marker-badge">
      <img src="/assets/logo-icon.png" alt="" draggable="false" />
    </div>
    <div class="loc-marker-tip"></div>`,
  iconSize: [52, 64],
  iconAnchor: [26, 60],
  popupAnchor: [0, -56],
});

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );
}

function popupHtml(loc: WorkshopLocation): string {
  const parts = [loc.naam, loc.adres, loc.stad].filter(Boolean).join(", ");
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
  const adres = loc.adres
    ? `<div class="loc-popup-adres">${escapeHtml(loc.adres)}${loc.stad ? `, ${escapeHtml(loc.stad)}` : ""}</div>`
    : "";
  const meta: string[] = [];
  if (loc.typeRuimte) meta.push(escapeHtml(loc.typeRuimte));
  return `
    <div class="loc-popup">
      <strong>${escapeHtml(loc.naam)}</strong>
      ${adres}
      ${meta.length ? `<div class="loc-popup-meta">${meta.join(" · ")}</div>` : ""}
      <a href="${mapsLink}" target="_blank" rel="noopener noreferrer">Open in Google Maps →</a>
    </div>`;
}

export type LocationsMapHandle = {
  focusLocation: (id: string) => void;
};

export const LocationsMap = forwardRef<
  LocationsMapHandle,
  { locations: WorkshopLocation[] }
>(function LocationsMap({ locations }, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
    }).setView([51.05, 3.95], 9);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current.clear();

    const valid = locations.filter(
      (l): l is WorkshopLocation & { lat: number; lng: number } =>
        typeof l.lat === "number" && typeof l.lng === "number",
    );
    if (!valid.length) return;

    const markers: L.Marker[] = [];
    valid.forEach((loc) => {
      const marker = L.marker([loc.lat, loc.lng], { icon: MARKER_ICON }).addTo(map);
      marker.bindPopup(popupHtml(loc));
      markersRef.current.set(loc.id, marker);
      markers.push(marker);
    });

    if (markers.length === 1) {
      map.setView(markers[0].getLatLng(), 14);
    } else {
      const group = L.featureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.25));
    }
  }, [locations]);

  useImperativeHandle(
    ref,
    () => ({
      focusLocation: (id: string) => {
        const map = mapRef.current;
        const marker = markersRef.current.get(id);
        if (!map || !marker) return;
        containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        map.flyTo(marker.getLatLng(), 15, { duration: 0.6 });
        window.setTimeout(() => marker.openPopup(), 650);
      },
    }),
    [],
  );

  return <div ref={containerRef} className="loc-map" />;
});
